// Static registry of artists with a dedicated detail page.
// Keyed by a slug derived from the track artist name. Only tracks whose
// artist name resolves here will render as a clickable tile on the homepage.
//
// To add a new artist page: drop the background asset into /public/img or
// /public/media, add an entry below, and the Featured Artist tile becomes a
// link to /artist/:slug automatically.
//
// Menu structure per artist:
//   menu: {
//     new_releases: [{ label, url }, ...],
//     playlist:     [{ label, url }, ...],
//     video:        [{ label, url }, ...],
//     upcoming:     [{ label, url }, ...],
//   }
export const ARTIST_MENU_SECTIONS = [
  { key: "new_releases", label: "New Releases" },
  { key: "playlist",     label: "Purchase Artist Music" },
  { key: "video",        label: "Music / Promo Video" },
  { key: "upcoming",     label: "Upcoming Release" },
];

const emptyMenu = () => ({ new_releases: [], playlist: [], video: [], upcoming: [] });

export const ARTIST_PAGES = {
  shinsengumi: {
    slug: "shinsengumi",
    name: "Shinsengumi",
    tagline: "Cosmic soul from the Black Box Music underground.",
    synopsis:
      "Shinsengumi carves a lane where dusk-lit R&B melts into cinematic hip-hop — smoke-soft vocals over widescreen production. Every drop reads like a night drive: unhurried, honest, and built for the after-hours. The music leans into themes of self-mastery, memory, and the small victories that pile up into a life — songs to keep close on the low days and turn all the way up on the good ones.",
    background: { type: "image", src: "/img/shinsengumi_bg.jpg", fit: "cover" },
    // Pinned "spotlight" cards — rendered stacked on the right column of the artist page.
    // Add one entry per major release / drop announcement.
    spotlights: [
      {
        title: "Give it to me Baby",
        artist: "Shinsengumi",
        subtitle: "Major Platform Release",
        cover: "/img/shinsengumi_give_it_to_me_baby.jpg",
        cta: "Listen Now",
        url: "https://distrokid.com/hyperfollow/shinsengumi/give-it-to-me-baby",
      },
      {
        title: "Glory Days",
        artist: "Shinsengumi",
        subtitle: "Major Platform Release",
        cover: "/img/shinsengumi_glory_days_release.jpg",
        cta: "Listen Now",
        url: "https://distrokid.com/hyperfollow/shisengumi/glory-days?ref=release",
      },
    ],
    menu: {
      new_releases: [
        {
          label: "Glory Days",
          cover: "/img/shinsengumi_glory_days_release.jpg",
          url: "https://blackboxradio.com/api/uploads/audio/cfd44394-8960-43b4-ac4d-22c378665b25",
          full_url: "https://music.apple.com/us/album/glory-days-single/1879443133",
        },
        {
          label: "Give it to me Baby",
          cover: "/img/shinsengumi_give_it_to_me_baby.jpg",
          url: "https://blackboxradio.com/api/uploads/audio/cec39d25-2333-4c16-aaf7-9bef87030c4d",
          full_url: "https://distrokid.com/hyperfollow/shinsengumi/give-it-to-me-baby",
        },
      ],
      playlist: [
        {
          label: "Glory Days",
          subtitle: "Shinsengumi · Apple Music",
          cover: "/img/shinsengumi_glory_days_release.jpg",
          url: "https://music.apple.com/us/album/glory-days-single/1879443133",
        },
        {
          label: "Give it to me Baby",
          subtitle: "Shinsengumi · Apple Music",
          cover: "/img/shinsengumi_give_it_to_me_baby.jpg",
          url: "https://music.apple.com/us/album/give-it-to-me-baby-single/6793869307",
        },
      ],
      video: [
        {
          label: "Glory Days (Music Video)",
          subtitle: "YouTube",
          cover: "/img/shinsengumi_glory_days_release.jpg",
          url: "https://youtu.be/oHxPkMxw3LY?si=OEOPKXpbtNb9YAvg",
        },
      ],
      upcoming: [],
    },
  },
  chioma: {
    slug: "chioma",
    name: "Chioma",
    tagline: "Freedom-day anthems for a Black and Proud future.",
    synopsis:
      "Chioma writes anthems for the moments that matter — freedom days, family reunions, and the quiet triumphs of just being seen. Her sound braids soulful vocals with contemporary R&B, gospel undertones, and West-African rhythms into a signature ELEVATION-era palette. The lyrics move between celebration and testimony: We Are The Change, Everybody (Black and Proud), and the songs still to come are built to sit in the culture — songs that carry a message, a memory, and a mirror all at once.",
    background: {
      type: "video",
      // Optional image rendered BEHIND the video (fills the whole page). Great when
      // the video is portrait (letterboxed) — the backdrop fills the side gaps.
      backdrop: "/img/chioma_elevation.webp",
      // Start the video after N ms so the backdrop image has time to breathe first.
      autoplayDelayMs: 9000,
      sources: [
        { src: "/media/chioma_bg.webm", type: "video/webm" },
        { src: "/media/chioma_bg.mp4", type: "video/mp4" },
      ],
      fit: "contain",
    },
    spotlights: [
      {
        title: "We Are The Change",
        artist: "Chioma",
        subtitle: "Major Platform Release",
        cover: "/img/chioma_we_are_the_change.jpg",
        cta: "Listen Now",
        url: "https://music.apple.com/us/album/we-are-the-change-chioma-feat-chioma/6775311379?i=6775311380",
      },
      {
        title: "Everybody (Black and Proud)",
        artist: "Chioma",
        subtitle: "Major Platform Release",
        cover: "/img/chioma_everybody.png",
        cta: "Listen Now",
        url: "https://music.apple.com/us/album/everybody-black-and-proud-single/6783539408",
      },
      {
        title: "Watch Me Move",
        artist: "Chioma",
        subtitle: "Major Platform Release",
        cover: "/img/chioma_watch_me_move.png",
        cta: "Listen Now",
        url: "https://music.apple.com/us/album/watch-me-move-single/6798143298",
      },
    ],
    menu: {
      new_releases: [
        {
          label: "We Are The Change",
          cover: "/img/chioma_we_are_the_change.jpg",
          url: "https://blackboxradio.com/api/uploads/audio/cad90a55-49b6-4cee-ae9b-5d805c2bfc62",
        },
        {
          label: "Everybody (Black and Proud)",
          cover: "/img/chioma_everybody.png",
          url: "https://youtu.be/OTpTuMrfh_8?is=sXff3n_U6ldn_odJ",
          full_url: "https://music.apple.com/us/album/everybody-black-and-proud-single/6783539408",
        },
      ],
      playlist: [
        {
          label: "We Are The Change",
          subtitle: "Chioma · Apple Music",
          cover: "/img/chioma_we_are_the_change.jpg",
          url: "https://music.apple.com/us/album/we-are-the-change-chioma-feat-chioma/6775311379?i=6775311380",
        },
        {
          label: "Everybody (Black and Proud)",
          subtitle: "Chioma · Apple Music",
          cover: "/img/chioma_everybody.png",
          url: "https://music.apple.com/us/album/everybody-black-and-proud-single/6783539408",
        },
      ],
      video: [
        {
          label: "Chioma · Reel",
          subtitle: "Instagram",
          cover: "/img/chioma_we_are_the_change.jpg",
          url: "https://www.instagram.com/reel/DaCP_q1N7TW/?utm_source=ig_web_button_share_sheet",
        },
      ],
      upcoming: [
        {
          label: "Chioma Debut Album 'ELEVATION'",
          subtitle: "Coming Soon on all Platforms!",
          cover: "/img/chioma_elevation.webp",
        },
      ],
    },
  },
  "africa-top-team": {
    slug: "africa-top-team",
    name: "Africa Top Team",
    tagline: "Building a sustainable fighter development ecosystem in Africa — powered by Warriors Culture Coin.",
    synopsis:
      "Africa Top Team is more than a fight camp — it is a continent-wide movement to develop the next generation of African champions with dignity, discipline, and long-term opportunity. From grassroots gyms to global title runs, ATT provides world-class coaching, nutrition, medical, and management infrastructure so athletes can compete on the biggest stages without leaving their heritage behind. Powered by Warriors Culture Coin, the ecosystem funnels resources directly into fighter development, community programs, and the culture that keeps warriors sharp.",
    background: {
      type: "video",
      // Still image rendered BEHIND the video — fills the whole page so portrait
      // videos never leave black bars on the sides.
      backdrop: "/img/att_backdrop.png",
      sources: [
        { src: "/media/att_bg.webm", type: "video/webm" },
        { src: "/media/att_bg.mp4", type: "video/mp4" },
      ],
      fit: "contain",
    },
    spotlights: [
      {
        title: "We Are The Change EP",
        artist: "Africa Top Team",
        subtitle: "Major Platform Release",
        cover: "/img/att_we_are_the_change.png",
        cta: "Listen Now",
        url: "https://music.apple.com/us/album/we-are-the-change/6775311379",
      },
      {
        title: "African Warrior Culture EP",
        artist: "Africa Top Team",
        subtitle: "Major Platform Release",
        cover: "/img/africa_top_team.jpg",
        cta: "Listen Now",
        url: "https://music.apple.com/us/album/african-warrior-culture-ep/1882755905",
      },
    ],
    menu: {
      new_releases: [
        {
          label: "We Are The Change — Chima",
          cover: "/img/africa_top_team.jpg",
          url: "https://blackboxradio.com/api/uploads/audio/68ae4060-3747-4ad6-8061-3b286405a4bd",
        },
        {
          label: "Mighty People — Africa Top Team",
          cover: "/img/africa_top_team.jpg",
          url: "https://youtu.be/vKrKClOEajc?si=lZqIjpUSGs8raPNb",
        },
        {
          label: "African Warriors Unite — Africa Top Team (Black Box Music)",
          cover: "/img/africa_top_team.jpg",
          url: "https://youtu.be/eTweA1T6EtU?is=D4Gs9uL9rGC8pWjg",
        },
      ],
      playlist: [
        {
          label: "We Are The Change EP",
          subtitle: "Africa Top Team · Apple Music",
          cover: "/img/att_we_are_the_change.png",
          url: "https://music.apple.com/us/album/we-are-the-change/6775311379",
        },
        {
          label: "African Warrior Culture EP",
          subtitle: "Africa Top Team · Apple Music",
          cover: "/img/africa_top_team.jpg",
          url: "https://music.apple.com/us/album/african-warrior-culture-ep/1882755905",
        },
      ],
      video: [
        {
          label: "Fight For Our Future — Africa Top Team (Black Box Music)",
          subtitle: "YouTube",
          cover: "/img/africa_top_team.jpg",
          url: "https://youtu.be/-Ft32aqmD5o?is=Tp_ff9TcFQmYqYpj",
        },
      ],
      upcoming: [],
    },
  },
};

export const artistSlug = (artistName) => {
  if (!artistName) return null;
  const s = artistName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return ARTIST_PAGES[s] ? s : null;
};

export const getArtistPage = (slugOrName) => {
  if (!slugOrName) return null;
  const key = slugOrName.toLowerCase();
  if (ARTIST_PAGES[key]) return ARTIST_PAGES[key];
  const s = artistSlug(slugOrName);
  return s ? ARTIST_PAGES[s] : null;
};
